import { useEffect } from "react";

interface SeoProps {
  title: string;
  description: string;
  keywords: string;
  path: string;
  type?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const OG_IMAGE =
  "https://readdy.ai/api/search-image?query=dark%20moody%20abstract%20desk%20with%20scattered%20documents%20and%20warm%20amber%20light%20rays%2C%20investigative%20editorial%20atmosphere%2C%20crimson%20accents%2C%20cinematic%20grain%2C%20no%20text&width=1200&height=630&seq=chigua-og-share&orientation=landscape";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function Seo({ title, description, keywords, path, type = "website", jsonLd }: SeoProps) {
  const rawBase = import.meta.env.BASE_URL || "/";
  const base = rawBase === "/" ? "" : rawBase.replace(/\/$/, "");
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonical = `${origin}${base}${path}`;
  const ldKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    document.title = title;

    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", "index, follow");
    upsertMeta("name", "author", "131吃瓜");
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", "131吃瓜");
    upsertMeta("property", "og:locale", "zh_CN");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", OG_IMAGE);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", OG_IMAGE);

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    document.head.querySelectorAll('script[data-seo-ld="true"]').forEach((node) => node.remove());
    if (ldKey) {
      const parsed = JSON.parse(ldKey) as Record<string, unknown> | Record<string, unknown>[];
      const blocks = Array.isArray(parsed) ? parsed : [parsed];
      blocks.forEach((block) => {
        const script = document.createElement("script");
        script.setAttribute("type", "application/ld+json");
        script.setAttribute("data-seo-ld", "true");
        script.textContent = JSON.stringify(block);
        document.head.appendChild(script);
      });
    }
  }, [title, description, keywords, type, canonical, ldKey]);

  return null;
}